/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the activity library as list and kanban views.
 * @state-owner navigation store owns filters and display options; dataset store owns activities
 * @allowed-imports React; workspace and dataset stores; shared components, selectors and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects navigation, context menus and dialogs
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { MouseEvent } from 'react';
import { CommandMenuRows, MenuRows } from '../../../shared/components/ContextMenu';
import { FilterBar, useDisplayOptions } from '../../../shared/components/FilterBar';
import { pointerPoint, useOverlay } from '../../../shared/components/OverlayHost';
import { Page, PageBody } from '../../../shared/components/PageShell';
import { NameCell, StatusIcon, StatusText } from '../../../shared/components/StatusIcon';
import { GearIcon } from '../../../shared/components/VispoIcon';
import { tableCells, VTable } from '../../../shared/components/VTable';
import { filterDefinitions } from '../../../shared/lib/filters';
import { clickable } from '../../../shared/lib/interactive';
import type { ActivityRecord } from '../../../shared/lib/model';
import { myClassIndexes } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useActivityActions } from '../model/use-activity-actions';
import { useActivityFilter } from '../model/use-activity-filter';
import { AssignmentDialog } from './AssignmentDialog';
import { TagDialog } from './TagDialog';

const BOARD_COLUMNS: readonly (readonly [ActivityRecord['status'], string])[] = [
  ['parsing', '解析中'],
  ['review', '待確認'],
  ['ready', '可用'],
];

export function ActivityBoard() {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useActivityFilter();
  const display = useDisplayOptions('act');
  const pick = useWorkspaceUiStore((state) => state.pick);
  const definitions = filterDefinitions('act', classes, myClassIndexes(classes));
  const { openCardMenu } = useActivityMenus();

  const sorted = [...activities].sort((a, b) => {
    if (display.ord === 'name') return a.name.localeCompare(b.name);
    if (display.ord === 'new') return b.createdAt - a.createdAt;
    if (display.ord === 'upd') return b.updatedAt - a.updatedAt;
    if (display.ord === 'tag') {
      /** Untagged material sorts last rather than first, where it would bury the rest. */
      const left = a.tags[0] ?? '\uffff';
      const right = b.tags[0] ?? '\uffff';
      return left.localeCompare(right) || a.name.localeCompare(b.name);
    }
    if (display.ord === 'st') {
      const order = ['parsing', 'review', 'ready'];
      return order.indexOf(a.status) - order.indexOf(b.status);
    }
    return 0;
  });

  /** Archived material leaves the working columns but keeps its own, so nothing is ever lost. */
  const working = sorted.filter((entry) => !entry.archived);
  const archived = sorted.filter((entry) => entry.archived);

  const columns =
    display.grp === 'ci'
      ? [
          ...classes.map((item, index) => ({
            title: item.name,
            icon: null as React.ReactNode,
            rows: working.filter((entry) => entry.usedByClassIndexes.includes(index)),
          })),
          {
            title: t('未加入班級'),
            icon: null as React.ReactNode,
            rows: working.filter((entry) => entry.usedByClassIndexes.length === 0),
          },
          { title: t('已封存'), icon: null as React.ReactNode, rows: archived },
        ]
      : [
          ...BOARD_COLUMNS.map(([status, label]) => ({
            title: t(label),
            icon: (
              <StatusIcon
                glyph={status === 'ready' ? 'done' : status === 'review' ? 'warn' : 'half'}
              />
            ),
            rows: working.filter((entry) => entry.status === status),
          })),
          {
            title: t('已封存'),
            icon: <StatusIcon glyph="todo" />,
            rows: archived,
          },
        ];

  return (
    <Page>
      <div className="vtbar">
        <FilterBar definitions={definitions} scope="act" />
      </div>
      {display.view === 'list' ? (
        <PageBody>
          <VTable
            empty={t('沒有符合的')}
            head={[t('教材'), t('題數'), t('班級'), t('答案來源'), t('狀態')]}
            rows={sorted.map((activity) => ({
              key: activity.id,
              onSelect: () => pick(activity.id),
              onContextMenu: (event) => openCardMenu(event, activity),
              cells: tableCells(
                <NameCell icon={t('題字')} text={activity.name} />,
                <span className="vmut">{`${activity.questionCount} ${t('題')}`}</span>,
                activity.usedByClassIndexes.length ? (
                  <span className="vchip">{classes[activity.usedByClassIndexes[0]].name}</span>
                ) : (
                  <span className="vmut">{t('未加入班級')}</span>
                ),
                activity.answerSource === 'ai' ? (
                  <span className="vcai">{t('AI 生成')}</span>
                ) : (
                  <span className="vmut">{t('有答案檔')}</span>
                ),
                activity.status === 'ready' ? (
                  <StatusText glyph="done" label={t('可用')} />
                ) : activity.status === 'review' ? (
                  <StatusText glyph="warn" label={t('待確認')} tone="wn" />
                ) : (
                  <StatusText glyph="half" label={t('解析中')} tone="pr" />
                ),
              ),
            }))}
            template="minmax(0,1fr) 90px 150px 130px 110px"
          />
        </PageBody>
      ) : (
        <div className="vbd">
          {columns.map((column) => (
            <div className="vbc" key={column.title}>
              <div className="vbch">
                {column.icon}
                <b>{column.title}</b>
                <span className="vbn">{column.rows.length}</span>
              </div>
              {/* The card list scrolls on its own so the column header stays put and the board's
                  horizontal scrollbar sits at the bottom of the viewport, not under the tallest
                  column. */}
              <div className="vbcl">
                {column.rows.length ? (
                  column.rows.map((activity) => (
                    <ActivityCard activity={activity} key={activity.id} onMenu={openCardMenu} />
                  ))
                ) : (
                  <div className="vbe">{t('沒有')}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

function ActivityCard({
  activity,
  onMenu,
}: {
  activity: ActivityRecord;
  onMenu(event: MouseEvent<HTMLElement>, activity: ActivityRecord): void;
}) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const display = useDisplayOptions('act');
  const pick = useWorkspaceUiStore((state) => state.pick);
  const classNames = activity.usedByClassIndexes.map((index) => classes[index].name);

  return (
    // biome-ignore lint/a11y/useSemanticElements: the row hosts its own buttons, so it cannot itself be a <button>
    <div
      className="vcard"
      onContextMenu={(event) => onMenu(event, activity)}
      role="button"
      tabIndex={0}
      {...clickable(() => pick(activity.id))}
    >
      <div className="vch">
        <span className="vcav" title={t('王維青')}>
          {t('師')}
        </span>
        <span className="vcid">{activity.id.toUpperCase()}</span>
        {activity.answerSource === 'ai' ? <span className="vcai">AI 答案</span> : null}
        <div className="sp" />
        {activity.status === 'parsing' ? (
          <span className="vcpct">{`${activity.parsedPercent ?? 0}%`}</span>
        ) : activity.status === 'review' ? (
          <span className="vcwarn">{`${activity.needsReview ?? 0} ${t('題待確認')}`}</span>
        ) : (
          <StatusIcon glyph="done" />
        )}
        <button
          className="vcog"
          title={t('更多')}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onMenu(event, activity);
          }}
        >
          <GearIcon />
        </button>
      </div>
      <h4>{activity.name}</h4>
      {activity.tags.length ? (
        <div className="vctags">
          {activity.tags.map((tag) => (
            <span className="vctag" key={tag}>
              {tag}
            </span>
          ))}
        </div>
      ) : null}
      {activity.status === 'parsing' ? (
        <span className="mtr" style={{ height: 3 }}>
          <i style={{ width: `${activity.parsedPercent ?? 0}%` }} />
        </span>
      ) : null}
      <div className="vcm">
        {display.props.includes('q') ? (
          <span className="vcmi">{`${activity.questionCount} ${t('題')}`}</span>
        ) : null}
        {display.props.includes('cls') ? (
          classNames.length ? (
            <span className="vchip">{classNames[0]}</span>
          ) : (
            <span className="vcmi">{t('未加入班級')}</span>
          )
        ) : null}
        {display.props.includes('ans') && activity.answerSource === 'ai' ? (
          <span className="vcmi">{t('AI 生成')}</span>
        ) : null}
        <div className="sp" />
        {display.props.includes('upd') ? (
          <span className="vcmi">{t(activity.updatedLabel)}</span>
        ) : null}
      </div>
    </div>
  );
}

/** The card menu and its class-picker sub-popover. */
export function useActivityMenus() {
  const t = useTranslator();
  const { openModal, openPopover } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const pick = useWorkspaceUiStore((state) => state.pick);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const actions = useActivityActions();

  const openCopyMenu = (event: MouseEvent<HTMLElement>, activity: ActivityRecord) => {
    const rect = event.currentTarget.getBoundingClientRect();
    window.setTimeout(
      () =>
        openPopover({
          x: Math.max(14, rect.right - 320),
          y: rect.bottom + 8,
          className: 'cmenu',
          content: (
            <CommandMenuRows
              heading={t('複製到哪個班')}
              items={classes.map((item, index) => ({
                icon: t('班'),
                title: item.name,
                description: activity.usedByClassIndexes.includes(index)
                  ? '已經在這個班'
                  : `${item.students.length} 人`,
                disabled: activity.usedByClassIndexes.includes(index),
                onSelect: () => actions.copyToClass(activity.id, index),
              }))}
            />
          ),
        }),
      10,
    );
  };

  const openCardMenu = (event: MouseEvent<HTMLElement>, activity: ActivityRecord) => {
    event.preventDefault();
    event.stopPropagation();
    const point = pointerPoint(event);
    openPopover({
      x: point.x,
      y: point.y,
      className: 'cmenu rmenu',
      content: (
        <MenuRows
          items={[
            { label: t('開啟'), shortcut: '↵', onSelect: () => pick(activity.id) },
            {
              label: t('發放'),
              onSelect: () => openModal(<AssignmentDialog activityId={activity.id} />),
            },
            { label: t('加入班級'), onSelect: () => openCopyMenu(event, activity) },
            { label: t('標籤'), onSelect: () => openModal(<TagDialog activity={activity} />) },
            {
              label: activity.archived ? t('取消封存') : t('封存'),
              onSelect: () => {
                useDatasetStore
                  .getState()
                  .updateActivity(activity.id, { archived: !activity.archived });
                notify(activity.archived ? t('已取消封存') : t('已封存'));
              },
            },
            {
              label: t('複製一份'),
              shortcut: '⌘ D',
              onSelect: () => actions.duplicate(activity.id),
            },
            'divider',
            { label: t('刪除'), danger: true, onSelect: () => actions.remove(activity.id) },
          ]}
        />
      ),
    });
  };

  return { openCardMenu, openCopyMenu };
}
